import { useEffect } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { queryKeys, type PluginManifest } from '@vtak/shared';
import { CLIENT_CONFIG } from '@/config';
import { authHeaders } from '@/services/api-client';
import { usePluginStore } from '@/stores/plugin-store';
import { useWorkspaceStore } from '@/stores/workspace-store';

/**
 * Loads the per-unit plugin manifest (staleTime ∞ — config is fixed per active
 * unit; a unit switch reloads the app) and mirrors it into the plugin store and
 * workspace store, which non-React modules read imperatively.
 */
export function useConfigQuery(): UseQueryResult<PluginManifest> {
  const selectedUnitId = useWorkspaceStore((s) => s.selectedUnitId);
  const query = useQuery({
    queryKey: queryKeys.workspace.config(selectedUnitId),
    staleTime: Infinity,
    queryFn: () => fetchPluginManifest(selectedUnitId),
  });

  const setConfig = usePluginStore((s) => s.setConfig);
  const applyManifest = useWorkspaceStore((s) => s.applyManifest);
  useEffect(() => {
    if (query.data) {
      setConfig(query.data);
      applyManifest(query.data);
    }
  }, [query.data, setConfig, applyManifest]);

  return query;
}

/** Fetch the plugin manifest for a unit (throws on failure so Query retries). */
async function fetchPluginManifest(unitId: string | null): Promise<PluginManifest> {
  const suffix = unitId ? `?unit=${encodeURIComponent(unitId)}` : '';
  const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/config${suffix}`, authHeaders());
  if (!res.ok) throw new Error(`config request failed: ${res.status}`);
  return (await res.json()) as PluginManifest;
}
